import packageJson from '../../package.json';
import { BUS_ACCESS_TOKEN, BUS_GLOBAL_LANGUAGE, BUS_USER_INFO, type ISgConnectUserInfo } from './auth/bus-topics';
import { PRODUCTION_ENVIRONMENTS } from './constants';
import { getSgwtConnectLibrary, type WidgetEnvironmentExtended } from './sgwt-widgets-utils';

const packageVersion = packageJson.version;

export enum SgwtWidgetName {
  ACCOUNT_CENTER = 'sgwt-account-center',
  CONNECT = 'sgwt-connect',
  HELP_CENTER = 'sgwt-help-center',
  MINI_FOOTER = 'sgwt-mini-footer',
  SPLASH_SCREEN = 'sgwt-splash-screen',
  WEB_ANALYTICS = 'sgwt-web-analytics',
  SGWT_CHATBOT = 'sgwt-chatbot',
  //
  SGWT_CORE = 'sgwt-core',
}

type EventDetails = Record<string, string | number | boolean | null | undefined>;

interface IMonitoringEvent {
  applicationId?: string;
  cdn: string | null;
  date: string;
  domain: string;
  event: string;
  details?: EventDetails;
  language?: string;
  sgbs: string;
  url: string;
  userAgent: string;
  version: string;
  widget: SgwtWidgetName;
}

declare global {
  interface Window {
    __sgwtWidgetsEventsQueue?: {
      events: IMonitoringEvent[];
      timeoutId: number | null;
    };
  }
}

const API_ENDPOINTS: Record<WidgetEnvironmentExtended, string> = {
  development: 'https://sgwt-api-sgwt-monitoring-uat.sgmarkets.world.socgen/api/v1/monitoring/sgwt-widgets',
  internal_homologation: 'https://sgwt-api-sgwt-monitoring-uat.sgmarkets.world.socgen/api/v1/monitoring/sgwt-widgets',
  homologation: 'https://api-s-uat.sgmarkets.com/widget/webtoolkit/sgwt-monitoring/v1/monitoring/sgwt-widgets',
  internal_production: 'https://sgwt-api-sgwt-monitoring.sgmarkets.world.socgen/api/v1/monitoring/sgwt-widgets',
  production: 'https://api-s.sgmarkets.com/widget/webtoolkit/sgwt-monitoring/v1/monitoring/sgwt-widgets',
};

// 'developer.fr.world.socgen' is not accessed directly with the Azure XKS, as there is a reverse proxy in front of it...
// const AZURE_ACCESSIBILITY_CHECK_RESOURCE = 'https://developer.fr.world.socgen/favicon.ico';
// ...so we need to check the direct resource on Azure XKS instead.
const AZURE_ACCESSIBILITY_CHECK_RESOURCE = 'https://dx-prod-staff-portal.dx.prd.euw.gbis.sg-azure.com/shell/favicon.ico';

// Time in ms waited before sending the event(s) to the API...
const WAITING_TIME_BEFORE_SENDING_EVENTS = 5000;

function getFromWidgetBus<T = string>(topic: string): T | undefined {
  return window.SGWTWidgetConfiguration?.bus?.dangerouslyGetCurrentValue<T>(topic);
}

/**
 * Returns an object without the key that have a `undefined`, `null` or `""` value. **Works only on one level**.
 * If the object is empty, it returns `undefined`.
 */
function cleanObject(obj?: EventDetails | null): EventDetails | undefined {
  if (!obj) {
    return undefined;
  }
  const data = Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== ''));
  return Object.keys(data).length > 0 ? data : undefined;
}

function getAPIEndpoint(userInfo?: ISgConnectUserInfo): string {
  const isOnLAN = userInfo?.origin_network === 'LAN';

  const env: string | null | undefined = window.SGWTWidgetConfiguration ? window.SGWTWidgetConfiguration.environment : null;
  const isProd = !env || PRODUCTION_ENVIRONMENTS.includes(env.toLowerCase());

  if (isProd) {
    return isOnLAN ? API_ENDPOINTS.internal_production : API_ENDPOINTS.production;
  }
  return isOnLAN ? API_ENDPOINTS.internal_homologation : API_ENDPOINTS.homologation;
}

class SgwtWidgetsMonitoring {
  private apiEndpoint: string;
  private applicationId: string | undefined;
  private cdnUrls: Record<string, string | null>;
  private azureAccessibilityCheckDone = false;

  constructor() {
    if (!window.__sgwtWidgetsEventsQueue) {
      this.initializeQueue();
    }
    const userInfo = getFromWidgetBus<ISgConnectUserInfo>(BUS_USER_INFO);
    this.apiEndpoint = getAPIEndpoint(userInfo);
    this.cdnUrls = {};

    // If the userInfo was not known on the initialization of the widget monitoring,
    // we listen to the bus for it and update the API endpoint accordingly...
    if (!userInfo && window.SGWTWidgetConfiguration?.bus) {
      const listener = window.SGWTWidgetConfiguration.bus.subscribe(BUS_USER_INFO, (info: ISgConnectUserInfo | undefined) => {
        if (info) {
          this.apiEndpoint = getAPIEndpoint(info);
          this.checkAzureAccessibility();
          window.SGWTWidgetConfiguration.bus.unsubscribe(listener);
        }
      });
    }
  }

  private initializeQueue() {
    window.__sgwtWidgetsEventsQueue = {
      events: [],
      timeoutId: null,
    };
  }

  private getToken(): string | undefined | null {
    // Get the sgwtConnect from the widget or from the window object...
    const sgwtConnect: any = getSgwtConnectLibrary();
    // Get the authorization token...
    if (sgwtConnect) {
      return sgwtConnect.getAuthorizationHeader();
    }
    const fromBus = getFromWidgetBus(BUS_ACCESS_TOKEN);
    return fromBus || null;
  }

  private sendEvents() {
    if (!window.__sgwtWidgetsEventsQueue) {
      return;
    }
    if (window.__sgwtWidgetsEventsQueue.events.length > 0) {
      const events = [...window.__sgwtWidgetsEventsQueue.events];
      window.__sgwtWidgetsEventsQueue.events = [];
      const token = this.getToken();
      if (token) {
        const options: any = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify(events),
        };
        fetch(this.apiEndpoint, options).catch((err) => console.log('monitoring not sent due to error', err));
      } else {
        console.debug('monitoring not sent due to missing token');
      }
    }
    window.__sgwtWidgetsEventsQueue.timeoutId = null;
  }

  private getCdnPath(widget: string): string | null {
    let path = this.cdnUrls[widget];
    if (path !== undefined) {
      return path;
    }
    const scriptTag = document.querySelector<HTMLScriptElement>(`script[src*="${widget}.js"]`);
    if (scriptTag) {
      path = scriptTag.src;
    } else {
      path = null;
    }
    this.cdnUrls[widget] = path;
    return path;
  }

  private currentLocation() {
    const url = window.location.toString();
    const domain = url
      .substring(0, url.indexOf('/', 8)) // remove the sub-routes to keep the domain
      .replace(/^https?:\/\//, '') // remove the protocol part
      .replace(/:\d+/, ''); // remove the port
    return { url, domain };
  }

  public queueEvent(widget: SgwtWidgetName, eventName: string, eventDetails?: EventDetails | null) {
    if (!window.__sgwtWidgetsEventsQueue) {
      this.initializeQueue();
    }

    const language = getFromWidgetBus(BUS_GLOBAL_LANGUAGE);
    const event: IMonitoringEvent = {
      applicationId: this.applicationId,
      date: new Date().toISOString(),
      event: eventName,
      cdn: this.getCdnPath(widget),
      details: cleanObject(eventDetails) || undefined,
      language,
      userAgent: window.navigator.userAgent,
      sgbs: 'v4',
      version: packageVersion,
      widget,
      ...this.currentLocation(),
    };

    if (window.__sgwtWidgetsEventsQueue) {
      window.__sgwtWidgetsEventsQueue.events.push(event);
      if (window.__sgwtWidgetsEventsQueue.timeoutId) {
        // Some events are already in the queue, so we reset the timeout...
        window.clearTimeout(window.__sgwtWidgetsEventsQueue.timeoutId as number);
      }
      window.__sgwtWidgetsEventsQueue.timeoutId = window.setTimeout(() => this.sendEvents(), WAITING_TIME_BEFORE_SENDING_EVENTS);
    }
  }

  /**
   * Try to fetch a resource hosted on Azure and accessible through XKS, to check the accessibility of Azure from the user's network.
   * In case of an error, we record an dedicated event.
   */
  private checkAzureAccessibility() {
    if (this.azureAccessibilityCheckDone) {
      return;
    }
    const userInfo = getFromWidgetBus<ISgConnectUserInfo>(BUS_USER_INFO);
    // Make the check only for connected SG users.
    if (!userInfo || userInfo.is_sg_group_user !== 'true') {
      return;
    }
    this.azureAccessibilityCheckDone = true;
    fetch(AZURE_ACCESSIBILITY_CHECK_RESOURCE, { mode: 'no-cors' }).catch((error: Error) => {
      console.debug('[sgwt-widgets] Azure accessibility check failed', { error });
      this.queueEvent(SgwtWidgetName.SGWT_CORE, 'azure.accessibility.failure', { message: error?.message, stack: error?.stack });
    });
  }

  public widgetLoaded(widget: SgwtWidgetName, applicationId?: string | null, configuration?: EventDetails | null) {
    this.applicationId = applicationId || undefined;
    this.queueEvent(widget, 'loaded', configuration);
    this.checkAzureAccessibility();
  }
}

const monitoring = new SgwtWidgetsMonitoring();

export function startWidgetMonitoring(widget: SgwtWidgetName, applicationId?: string | null, configuration?: EventDetails | null) {
  monitoring.widgetLoaded(widget, applicationId, configuration);
}

export function registerEvent(widget: SgwtWidgetName, eventName: string, eventDetails?: EventDetails | null) {
  monitoring.queueEvent(widget, eventName, eventDetails);
}

export function registerAccountCenterEvent(eventName: string, eventDetails?: EventDetails | null) {
  registerEvent(SgwtWidgetName.ACCOUNT_CENTER, eventName, eventDetails);
}

export function registerMiniFooterEvent(eventName: string, eventDetails?: EventDetails | null) {
  registerEvent(SgwtWidgetName.MINI_FOOTER, eventName, eventDetails);
}

export function registerHelpCenterEvent(eventName: string, eventDetails?: EventDetails | null) {
  registerEvent(SgwtWidgetName.HELP_CENTER, eventName, eventDetails);
}

export function registerSplashScreenEvent(eventName: string, eventDetails?: EventDetails | null) {
  registerEvent(SgwtWidgetName.SPLASH_SCREEN, eventName, eventDetails);
}

export function registerChatbotEvent(eventName: string, eventDetails?: EventDetails | null) {
  registerEvent(SgwtWidgetName.SGWT_CHATBOT, eventName, eventDetails);
}

export function registerMainIssue(widget: SgwtWidgetName, error: Error) {
  registerEvent(widget, 'general-issue', { message: error.stack });
}
